import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ClipboardList } from 'lucide-react';
import {
  Card, Button, Input, Select, Badge, EmptyState, Modal, Label, Textarea, StatusDropdown,
  SkeletonListRows,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { listTasks, createTask, updateTask, deleteTask, listMatters, listEmployees } from '../lib/db';
import { formatDate, classNames } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

const PRIORITY_TONE = { low: 'slate', medium: 'blue', high: 'amber', critical: 'red' };
const TASK_STATUSES = [
  { value: 'pending',     label: 'Pending',     tone: 'slate' },
  { value: 'in_progress', label: 'In Progress', tone: 'blue'  },
  { value: 'completed',   label: 'Completed',   tone: 'green' },
  { value: 'cancelled',   label: 'Cancelled',   tone: 'red'   },
];
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...TASK_STATUSES.map((s) => ({ value: s.value, label: s.label })),
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const blank = () => ({
  title: '', description: '', priority: 'medium',
  status: 'pending', due_date: '', matter_id: '', employee_id: '',
});

export default function TasksPage() {
  const { firmId, refreshSignal } = useFirm();
  const { userProfile } = useAuth();
  const log = useActivityLogger();
  const [tasks, setTasks] = useState([]);
  const [matters, setMatters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    (async () => {
        try {
      const [{ data: t }, { data: m }, { data: e }] = await Promise.all([
        listTasks(firmId, {}),
        listMatters(firmId),
        listEmployees(firmId, { status: 'active' }),
      ]);
      setTasks(t || []);
      setMatters(m || []);
      setEmployees(e || []);
        } finally {
          setLoading(false);
        }
      })();
  }, [firmId, refreshSignal]);

  const filtered = useMemo(() => tasks
    .filter((t) => !statusFilter || t.status === statusFilter)
    .filter((t) => !q || t.title.toLowerCase().includes(q.toLowerCase())),
    [tasks, statusFilter, q]);

  const counts = useMemo(() => ({
    pending: tasks.filter((t) => t.status === 'pending').length,
    in_progress: tasks.filter((t) => t.status === 'in_progress').length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    overdue: tasks.filter((t) => t.due_date && t.status !== 'completed' && t.status !== 'cancelled' && new Date(t.due_date) < new Date()).length,
  }), [tasks]);

  const openNew = () => { setForm(blank()); setShowNew(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      matter_id:   form.matter_id   || null,
      employee_id: form.employee_id || null,
      due_date:    form.due_date    || null,
      created_by:  userProfile?.id,
    };
    const { data, error } = await createTask(firmId, payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    log('task', data.id, 'created', `Task "${data.title}" created`);
    setTasks((prev) => [data, ...prev]);
    setShowNew(false);
  };

  const changeStatus = async (task, newStatus) => {
    const prev = tasks;
    setTasks((ts) => ts.map((t) => t.id === task.id ? { ...t, status: newStatus } : t));
    const { error } = await updateTask(task.id, {
      status: newStatus,
      completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
      completed_by: newStatus === 'completed' ? userProfile?.id : null,
    });
    if (error) { toast.error(error.message); setTasks(prev); return; }
    log('task', task.id, 'updated', `Task "${task.title}" marked ${newStatus}`);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await deleteTask(id);
    log('task', id, 'deleted', 'Task deleted');
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const isOverdue = (t) =>
    t.due_date && t.status !== 'completed' && t.status !== 'cancelled' && new Date(t.due_date) < new Date();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track to-dos across all matters</p>
        </div>
        <Button icon={Plus} onClick={openNew}>New task</Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Pending',     value: counts.pending,     color: 'text-slate-700 dark:text-slate-200' },
          { label: 'In progress', value: counts.in_progress, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Completed',   value: counts.completed,   color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Overdue',     value: counts.overdue,     color: 'text-red-600 dark:text-red-400' },
        ].map(({ label, value, color }) => (
          <Card key={label} className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
            <p className={classNames('text-3xl font-bold mt-1', color)}>{value}</p>
          </Card>
        ))}
      </div>

      {/* Filters + list */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
          <Input className="flex-1" placeholder="Search tasks…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={STATUS_OPTIONS} />
        </div>

        {loading ? (
          <SkeletonListRows rows={6} />
        ) : !filtered.length ? (
          tasks.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="Track work as tasks"
              description="Tasks are short to-do items you can attach to a matter — research, drafting, follow-ups. They show up on the matter's timeline and on the assignee's dashboard."
              action={<Button icon={Plus} onClick={openNew}>New task</Button>}
            />
          ) : (
            <EmptyState
              icon={ClipboardList}
              title="No tasks match your filters"
              description="Try clearing the status or assignee filter above."
              action={<Button icon={Plus} onClick={openNew}>New task</Button>}
            />
          )
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {filtered.map((task) => (
              <li key={task.id} className={classNames(
                'flex items-start gap-4 px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition',
                task.status === 'completed' && 'opacity-60',
              )}>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={classNames('text-sm font-medium text-slate-900 dark:text-slate-100', task.status === 'completed' && 'line-through')}>
                    {task.title}
                  </p>
                  {task.description && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">{task.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <StatusDropdown
                      value={task.status}
                      options={TASK_STATUSES}
                      onChange={(s) => changeStatus(task, s)}
                    />
                    <Badge tone={PRIORITY_TONE[task.priority]}>{task.priority}</Badge>
                    {task.matter && (
                      <span className="text-xs text-slate-400">{task.matter.matter_number} · {task.matter.title}</span>
                    )}
                    {task.due_date && (
                      <span className={classNames('text-xs', isOverdue(task) ? 'text-red-500 font-semibold' : 'text-slate-400')}>
                        Due {formatDate(task.due_date)}{isOverdue(task) ? ' · Overdue' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Delete */}
                <button onClick={() => remove(task.id)} className="mt-0.5 flex-shrink-0 text-slate-300 hover:text-red-500 transition">
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* New task modal */}
      {showNew && (
        <Modal open title="New task" onClose={() => setShowNew(false)}>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Draft motion to dismiss" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional details…" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} options={PRIORITY_OPTIONS} />
              </div>
              <div>
                <Label>Due date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Matter (optional)</Label>
              <Select
                value={form.matter_id}
                onChange={(e) => setForm({ ...form, matter_id: e.target.value })}
                options={[
                  { value: '', label: 'None' },
                  ...matters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` })),
                ]}
              />
            </div>
            <div>
              <Label>Assign to</Label>
              <Select
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                options={[
                  { value: '', label: 'Unassigned' },
                  ...employees.map((e) => ({ value: e.id, label: `${e.first_name} ${e.last_name || ''} — ${e.role}` })),
                ]}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button type="submit" icon={Plus} disabled={saving}>{saving ? 'Saving…' : 'Create task'}</Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
